/**
 * Map Privacy Utilities
 * Ensures host locations are never exposed on maps
 * Provides privacy-safe coordinate offsetting
 */

/**
 * Generates a consistent random offset for coordinates
 * Uses event ID as seed for consistency (same event = same offset)
 * Offset range: 30-80 meters in a random direction
 */
export function getPrivacyOffset(eventId: string): { latOffset: number; lngOffset: number } {
  // Use eventId as seed for consistent random generation
  let hash = 0;
  for (let i = 0; i < eventId.length; i++) {
    const char = eventId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  // Create seeded random function
  const random = () => {
    hash = (hash * 9301 + 49297) % 233280;
    return hash / 233280;
  };

  // Generate random angle (0-360 degrees)
  const angle = random() * 2 * Math.PI;

  // Generate random distance (30-80 meters)
  const minDistance = 30; // meters
  const maxDistance = 80; // meters
  const distance = minDistance + random() * (maxDistance - minDistance);

  // Convert meters to degrees (approximate)
  // At equator: 1 degree latitude ≈ 111km
  // Longitude varies by latitude, but we'll use a reasonable approximation
  const metersPerDegreeLat = 111000;
  const metersPerDegreeLng = 111000 * 0.8; // Rough approximation for mid-latitudes

  const latOffset = (distance * Math.cos(angle)) / metersPerDegreeLat;
  const lngOffset = (distance * Math.sin(angle)) / metersPerDegreeLng;

  return { latOffset, lngOffset };
}

/**
 * Gets display coordinates with privacy offset applied
 * Returns offset coordinates for map display while keeping real coords unchanged
 */
export function getDisplayCoordinates(
  event: { id: string; latitude: number; longitude: number; venueType?: string },
  isOwner: boolean,
  isAccepted: boolean
): { lat: number; lng: number } {
  // For venue-based events (public locations), show exact location to everyone
  if (event.venueType && event.venueType !== 'home') {
    return { lat: event.latitude, lng: event.longitude };
  }

  // For private/home locations, NEVER show exact location on map
  // Exact meetup point should be shared by host in Vybe chat
  // Apply privacy offset for everyone (including accepted members)
  const { latOffset, lngOffset } = getPrivacyOffset(event.id);
  return {
    lat: event.latitude + latOffset,
    lng: event.longitude + lngOffset,
  };
}

/**
 * Gets the radius circle parameters for map display
 * Extended radius to indicate general area without exposing exact location
 * @param context - 'explore' for map view with many events (smaller radius), 'detail' for single event view (larger radius)
 */
export function getPrivacyRadiusMeters(context: 'explore' | 'detail' = 'detail'): number {
  // Smaller radius for explore map view to reduce clutter when many events are shown
  if (context === 'explore') {
    return 50; // ~50 meter radius - more pin-like for map view
  }
  // Larger radius for individual vybe detail pages to show general area
  return 150; // ~150 meter radius to show general area while maintaining privacy
}

/**
 * Gets the styled circle options for the privacy radius
 */
export function getRadiusCircleStyle(): {
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeOpacity: number;
  strokeWeight: number;
} {
  // Branded pink color from design tokens
  return {
    fillColor: '#FF3B9A', // pink
    fillOpacity: 0.2, // 20% opacity for fill
    strokeColor: '#FF3B9A',
    strokeOpacity: 0.6, // 60% opacity for border
    strokeWeight: 2,
  };
}

/**
 * Determines if exact location should be shown
 */
export function shouldShowExactLocation(
  isOwner: boolean,
  isAccepted: boolean,
  venueType?: string
): boolean {
  // Always show exact location for venue-based events (public venues)
  if (venueType && venueType !== 'home') {
    return true;
  }

  // For private locations, never show exact location on map
  // Exact meetup point should be shared in Vybe chat
  return false;
}

/**
 * Gets the zoom level for displaying a single event
 */
export function getEventMapZoom(venueType?: string): number {
  // Closer zoom for venue events (exact location shown)
  if (venueType && venueType !== 'home') {
    return 16; // Street level
  }

  // Wider zoom for home events (area shown with radius)
  return 15; // Neighbourhood level
}

/**
 * Gets the map center and zoom for displaying multiple events
 */
export function getMultiEventMapBounds(
  events: Array<{ latitude: number; longitude: number }>,
  userLocation?: { lat: number; lng: number }
): {
  center: { lat: number; lng: number };
  zoom: number;
} | null {
  if (events.length === 0) {
    // No events, center on user if available
    if (userLocation) {
      return { center: userLocation, zoom: 13 };
    }
    return null;
  }

  // Calculate bounds of all events
  let minLat = events[0].latitude;
  let maxLat = events[0].latitude;
  let minLng = events[0].longitude;
  let maxLng = events[0].longitude;

  events.forEach(event => {
    minLat = Math.min(minLat, event.latitude);
    maxLat = Math.max(maxLat, event.latitude);
    minLng = Math.min(minLng, event.longitude);
    maxLng = Math.max(maxLng, event.longitude);
  });

  // Add user location to bounds if available
  if (userLocation) {
    minLat = Math.min(minLat, userLocation.lat);
    maxLat = Math.max(maxLat, userLocation.lat);
    minLng = Math.min(minLng, userLocation.lng);
    maxLng = Math.max(maxLng, userLocation.lng);
  }

  // Calculate center
  const center = {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };

  // Calculate zoom based on bounds
  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  let zoom = 13; // Default
  if (maxDiff < 0.01) zoom = 15; // Very close events
  else if (maxDiff < 0.05) zoom = 13; // Close events
  else if (maxDiff < 0.1) zoom = 12; // Medium distance
  else if (maxDiff < 0.5) zoom = 10; // Far apart
  else zoom = 9; // Very far apart

  return { center, zoom };
}
